import { useMemo } from 'react';

import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';

import type { Categorias } from '#components/mobile/informes/calculos';
import { aqlQuery } from '#queries/aqlQuery';

type Fila = { date: string; category: string | null; amount: IntegerAmount };

/**
 * Gasto de cada día (en positivo) a partir de las filas día × categoría.
 * Mismo criterio que el resto de Informes: sin categorías de ingreso,
 * excluidas ni ocultas; lo que no tiene categoría solo cuenta si es salida.
 */
export function gastoPorDia(
  filas: readonly Fila[],
  categorias: Categorias,
  excluidas: ReadonlySet<string>,
): Map<string, IntegerAmount> {
  const porDia = new Map<string, IntegerAmount>();
  for (const f of filas) {
    if (f.category == null) {
      if (f.amount >= 0) {
        continue;
      }
    } else {
      const info = categorias.get(f.category);
      if (!info || info.esIngreso || info.oculta || excluidas.has(f.category)) {
        continue;
      }
    }
    porDia.set(f.date, (porDia.get(f.date) ?? 0) - f.amount);
  }
  return porDia;
}

/** Gasto de cada día del mes `mes` (una consulta agrupada por día). */
export function useGastoDiario({
  mes,
  categorias,
  excluidas,
  activo = true,
}: {
  mes: string;
  categorias: Categorias;
  excluidas: ReadonlySet<string>;
  /** Sin activar no consulta (la tarjeta pequeña no dibuja la línea). */
  activo?: boolean;
}): { porDia: Map<string, IntegerAmount> | null; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['informes', 'gasto-diario', mes],
    staleTime: 30_000,
    enabled: activo,
    queryFn: async () => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            'account.offbudget': false,
            $and: [
              {
                $or: [
                  { 'payee.transfer_acct': null },
                  { 'payee.transfer_acct.offbudget': true },
                ],
              },
              // AQL solo aplica el primer operador de cada objeto.
              { date: { $gte: `${mes}-01` } },
              { date: { $lte: `${mes}-31` } },
            ],
          })
          .groupBy(['date', { $id: '$category' }])
          .select([
            'date',
            { category: { $id: '$category.id' } },
            { amount: { $sum: '$amount' } },
          ]),
      );
      return filas as Fila[];
    },
  });
  const porDia = useMemo(
    () => (data ? gastoPorDia(data, categorias, excluidas) : null),
    [data, categorias, excluidas],
  );
  return { porDia, isLoading };
}
